import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { ActionButton } from "../../components/ui/ActionButton";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card, CardContent } from "../../components/ui/Card";
import { EntityLink } from "../../components/ui/EntityLink";
import { formatDateTime, getEventWebUrl, languageName } from "../../utils/helpers";
import { CARD_SUMMARY_MAX_CHARS, reviewReasonLabel } from "./reviewReasons";

// The field families a `translations` row can belong to (`TRANSLATION_KINDS` in the API).
export const KIND_LABELS = {
  description: "Description",
  card_summary: "Card summary",
  venue_prose: "Venue details",
};

export const STATUS_LABELS = {
  needs_review: "Needs review",
  failed: "Failed",
  ok: "Served",
  same: "Already in that language",
};

const STATUS_VARIANTS = { needs_review: "warning", failed: "destructive", ok: "success", same: "secondary" };

export const rowId = (row) => `${row.kind}:${row.key}:${row.locale}`;

// A row the API can approve as is: a `needs_review` candidate with text (a refusal or a cut-off
// reply has none).
export const isApprovable = (row) => row.status === "needs_review" && Boolean(row.text?.trim());

// A row readers are served; a bulk retranslate leaves it, edits included, as it is (#839).
export const isServed = (row) => row.status === "ok";

// A row in the review queue's two lists, the only ones whose reason describes the stored text.
const isUnderReview = (row) => row.status === "needs_review" || row.status === "failed";

export const editorName = (row) => row.edited_by_email || row.edited_by;

// One document showing the text, linked into the admin in a new tab so the list keeps its place;
// an event also opens on the consumer site in the row's language, as its readers see it. The same
// text may appear on many: an action applies to all of them.
function Example({ example, locale, served }) {
  if (!example) return null;
  const label = example.label || example.id;
  const webUrl = example.type === "event" ? getEventWebUrl(example.id, { lang: locale }) : null;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
      <span>
        Shown on {example.type === "venue" ? "venue" : "event"}{" "}
        <EntityLink type={example.type} id={example.id} newTab>
          {label}
        </EntityLink>
        {example.status && example.status !== "active" && ` (${example.status})`}
      </span>
      {webUrl && (
        <a
          href={webUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground"
        >
          View as reader
          <ExternalLink className="h-3 w-3" />
          {/* Unknown (null) while the setting loads or could not be read: say nothing rather than guess. */}
          {served === false && <span>(not served to readers yet)</span>}
        </a>
      )}
    </p>
  );
}

// What the checks made of a suggestion in the box (#839): it is not saved until the admin saves it.
function suggestionNote(suggestion, locale) {
  if (suggestion.status === "same") return `Same as the source: it already reads as ${languageName(locale)}`;
  const verdict =
    suggestion.status === "ok" ? "Passed the checks" : `Flagged: ${reviewReasonLabel(suggestion.review_reason, locale)}`;
  return `Suggestion, not saved yet · ${verdict}`;
}

export function TranslationRow({ row, busy, selected, onSelect, served, showStatus, onSave, onApprove, onRetranslate, onDiscard }) {
  // Seeded from the stored text (empty for a failed row); the parent keys this component on the
  // row's status and `updated_at`, so a row rewritten since resets it.
  const stored = row.text ?? "";
  const [text, setText] = useState(stored);
  // A Retranslate's result, shown in the box until it is saved or cancelled; nothing is written.
  const [suggestion, setSuggestion] = useState(null);
  const id = rowId(row);
  // Shown only while the row is under review: a served row keeps the reason it was flagged for as
  // history, which no longer describes the text readers get.
  const reason = isUnderReview(row) ? reviewReasonLabel(row.review_reason, row.locale) : null;
  const overLimit = row.kind === "card_summary" && text.length > CARD_SUMMARY_MAX_CHARS;
  const changed = text !== stored;
  // One primary action: the stored candidate approved as it is, or the box's text saved as an edit.
  // Cancel shows while the box differs or a suggestion is open (one that matches the stored text too).
  // Either way the row ends served, reviewed by the admin.
  const approve = !changed && isApprovable(row);

  const showSuggestion = (result) => {
    setText(result.text);
    setSuggestion(result);
  };
  const cancel = () => {
    setText(stored);
    setSuggestion(null);
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {/* No `onSelect` where there is no bulk action to select for (the per-document panel, #840). */}
          {onSelect && (
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={selected}
              onChange={() => onSelect(row)}
              aria-label={`Select the ${languageName(row.locale)} ${KIND_LABELS[row.kind] ?? row.kind} translation`}
            />
          )}
          <Badge variant="secondary">{KIND_LABELS[row.kind] ?? row.kind}</Badge>
          <Badge variant="default">{languageName(row.locale)}</Badge>
          {showStatus && <Badge variant={STATUS_VARIANTS[row.status]}>{STATUS_LABELS[row.status] ?? row.status}</Badge>}
          {reason && (
            <Badge variant="warning" title={row.review_reason}>
              {reason}
            </Badge>
          )}
          {row.model && <span className="text-muted-foreground">{row.model}</span>}
          <span className="text-muted-foreground">· {formatDateTime(row.updated_at)}</span>
        </div>
        {row.edited_by && (
          <p className="text-xs text-muted-foreground">
            Reviewed by {editorName(row)} · {formatDateTime(row.updated_at)}
          </p>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              Source{row.source_lang ? ` (${languageName(row.source_lang)})` : ""}
            </p>
            <p className="whitespace-pre-wrap rounded-md border border-input bg-muted/30 px-3 py-2 text-sm">
              {row.source_text}
            </p>
            <Example example={row.example} locale={row.locale} served={served} />
          </div>
          <div>
            <label htmlFor={`translation-${id}`} className="mb-1 block text-xs font-medium text-muted-foreground">
              Translation into {languageName(row.locale)}
            </label>
            {suggestion && (
              <p className={`mb-1 text-xs ${suggestion.status === "needs_review" ? "text-amber-700" : "text-muted-foreground"}`}>
                {suggestionNote(suggestion, row.locale)}
              </p>
            )}
            <textarea
              id={`translation-${id}`}
              className="min-h-[5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            {row.kind === "card_summary" && (
              <p className={`text-right text-xs ${overLimit ? "font-medium text-red-700" : "text-muted-foreground"}`}>
                {text.length} / {CARD_SUMMARY_MAX_CHARS}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Saved as typed and served wherever this exact source text appears. No nightly run overwrites it.
          </p>
          <div className="flex flex-wrap gap-2">
            <ActionButton busy={busy} actionKey={`${id}:discard`} onClick={() => onDiscard(row)}>
              Discard
            </ActionButton>
            <ActionButton busy={busy} actionKey={`${id}:retranslate`} onClick={() => onRetranslate(row, showSuggestion)}>
              Retranslate
            </ActionButton>
            {(changed || suggestion) && (
              <Button variant="outline" size="sm" disabled={Boolean(busy)} onClick={cancel}>
                Cancel
              </Button>
            )}
            {approve ? (
              <ActionButton busy={busy} actionKey={`${id}:approve`} variant="default" onClick={() => onApprove(row)}>
                Approve
              </ActionButton>
            ) : (
              <ActionButton
                busy={busy}
                actionKey={`${id}:save`}
                variant="default"
                disabled={!changed || !text.trim()}
                onClick={() => onSave(row, text)}
              >
                Save
              </ActionButton>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
